import React, {FC, ReactElement, useEffect, useState} from 'react';
import './App.css';

import '@aws-amplify/ui/dist/style.css';

import "./store/mobxConfig"
import Amplify, {Auth, Hub} from 'aws-amplify';
import {userInfoStore} from "./store/UserInfo";
import {selectedCompanyStore} from "./store/SelectedCompany";
import awsconfig from './aws-exports';
import WakeUpLoader from "./loader/WakeUpLoader";
import FullScreenPage from "./views/FullScreenPage";
import OAuthComponent from "./OAuthComponent";
import {logEvent} from "./utils/SentryUtils";
import {ThemeProvider} from "@mui/material/styles";
import {theme} from "./views/theme";
import CssBaseline from "@mui/material/CssBaseline";
import AuthorizedApp from "./main/AuthorizedApp";
import {invoiceWizardState} from "./store/InvoiceWizardState";
import {uiStateStore} from "./store/UiState";
import {newEmployeeWizardState} from "./store/NewEmployeeWizardState";
import {I18nextProvider} from 'react-i18next';
import i18nNola from "./locale/i18";
import {accountEmailStateStore} from "./store/AccountEmailState";

// no typedefs for this one
//@ts-ignore
require('browser-unhandled-rejection').auto()

const oauth = {
    domain: 'nola-accounting.auth.us-east-2.amazoncognito.com',
    scope: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
    redirectSignIn: window.location.origin,
    redirectSignOut: window.location.origin,
    responseType: 'code'
};

const updatedAwsConfig = {
    ...awsconfig,
    oauth: {
        ...oauth
    }
}
Amplify.configure(updatedAwsConfig);

enum AuthState {
    LOADING,
    SIGNED_IN,
    NO_AUTH
}

export const App: FC = (): ReactElement => {
    const [authState, setAuthState] = useState(AuthState.LOADING);

    useEffect(() => {
        Hub.listen('auth', ({payload: {event, data}}) => {
            switch (event) {
                case 'signIn':
                case 'cognitoHostedUI':
                    getUser().then(() => setAuthState(AuthState.SIGNED_IN));
                    break;
                case 'signOut':
                    setAuthState(AuthState.NO_AUTH);
                    break;
                case 'signIn_failure':
                case 'cognitoHostedUI_failure':
                    logEvent("auth", `Sign in failure: ${data}`)
                    console.log('Sign in failure', data);
                    break;
            }
        });

        getUser()
                .then(() => setAuthState(AuthState.SIGNED_IN))
                .catch(() => {
                    logEvent("auth", "No token. Start logging-in")
                    setAuthState(AuthState.NO_AUTH)
                });
    }, []);

    useEffect(() => {
        if (authState === AuthState.NO_AUTH) {
            Auth.federatedSignIn()
        }
    }, [authState])

    useEffect(() => {
        return () => {
            selectedCompanyStore.stopPersisting();
            invoiceWizardState.stopPersisting();
            uiStateStore.stopPersisting();
            newEmployeeWizardState.stopPersisting();
            accountEmailStateStore.stopPersisting();
        }
    }, [])

    function getUser() {
        return Auth.currentAuthenticatedUser()
                .then(userInfo => {
                    userInfoStore.setEmail(userInfo.attributes.email)
                    logEvent("auth", "User is present. Logged-in")
                    return userInfo
                });
    }

    return (
            <div className="App">
                <ThemeProvider theme={theme}>
                    <CssBaseline/>
                    <I18nextProvider i18n={i18nNola}>
                        <FullScreenPage>
                            {authState !== AuthState.SIGNED_IN && <OAuthComponent/>}
                            {authState === AuthState.SIGNED_IN &&
                                    <WakeUpLoader>
                                        <AuthorizedApp/>
                                    </WakeUpLoader>
                            }
                        </FullScreenPage>
                    </I18nextProvider>
                </ThemeProvider>
            </div>
    );
}